import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Ghost, Lightning, Crown } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const fmtCountdown = (target) => {
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return 'fading…';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff / 60000) % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/**
 * Persistent banner that surfaces an active Ghost Bubble.
 * Anyone can attempt to catch it. Spawns a fullscreen celebration on success.
 */
const GhostBanner = () => {
  const [ghost, setGhost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caughtBubble, setCaughtBubble] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/ghost/current');
      setGhost(data.ghost);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const attempt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/ghost/catch');
      if (data.caught) {
        setCaughtBubble(data);
        if (navigator.vibrate) try { navigator.vibrate([80, 40, 80, 40, 200]); } catch {}
        toast.success(`You caught ${data.ghost_name}!`);
        load();
      } else {
        toast.message(data.hint || 'It slipped away…');
        if (navigator.vibrate) try { navigator.vibrate(30); } catch {}
      }
    } catch (e) {
      toast.message(e?.response?.data?.detail || 'no ghost roaming');
    } finally { setBusy(false); }
  };

  if (!ghost) return null;
  const stage = ghost.stage || 'rumored';
  const isCatchable = stage === 'catchable';
  const isRumored = stage === 'rumored';
  const stageMeta = {
    rumored:   { label: 'a rumor',     color: '#666', tone: 'rumored' },
    glimpsed:  { label: 'glimpsed',    color: '#FF007F', tone: 'glimpsed' },
    catchable: { label: 'catchable',   color: '#FFD700', tone: 'catchable' },
  }[stage];

  return (
    <>
      <motion.div
        initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="relative rounded-2xl p-4 flex items-center gap-3 overflow-hidden"
        data-testid={`ghost-banner-${stage}`}
        style={{
          background: isCatchable
            ? 'linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,0,127,0.15), rgba(0,0,0,0.5))'
            : isRumored
            ? 'linear-gradient(135deg, rgba(122,0,255,0.15), rgba(0,0,0,0.6))'
            : 'linear-gradient(135deg, rgba(255,0,127,0.2), rgba(122,0,255,0.2), rgba(0,0,0,0.5))',
          borderColor: `${stageMeta.color}55`, borderWidth: 1,
        }}
      >
        <span className="absolute inset-0 pointer-events-none opacity-40"
              style={{ background: `radial-gradient(circle at 15% 30%, ${stageMeta.color}55, transparent 50%)` }} />
        <div className="relative h-14 w-14 rounded-full overflow-hidden ring-2 shrink-0"
             style={{ borderColor: `${stageMeta.color}55`, ringColor: `${stageMeta.color}55` }}>
          {bubbleImageSrc(ghost) ? (
            <img src={bubbleImageSrc(ghost)} alt={ghost.name}
                 className="h-full w-full object-cover"
                 style={{
                   filter: isRumored ? 'blur(8px) brightness(0.5)'
                         : !isCatchable ? 'blur(2px) brightness(0.75) hue-rotate(15deg)'
                         : 'hue-rotate(15deg) saturate(1.3)',
                 }} />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-purple-900 to-black" />
          )}
          {isCatchable && (
            <span className="absolute inset-0 rounded-full ring-1 ring-bx-gold/40 animate-pulse" />
          )}
        </div>
        <div className="flex-1 min-w-0 relative z-10">
          <div className="text-[10px] uppercase tracking-widest flex items-center gap-1"
               style={{ color: stageMeta.color }}>
            <Ghost size={12} weight="fill" /> {stageMeta.label} · fades in {fmtCountdown(ghost.expires_at)}
          </div>
          <div className="font-display text-lg sm:text-xl font-black tracking-tighter italic">
            {isRumored ? <span className="text-white/60">"a name nobody dares say"</span>
                       : <span>"{ghost.name}"</span>}
          </div>
          <div className="text-white/60 text-xs italic">
            {isRumored && 'whispers grow stronger every hour…'}
            {stage === 'glimpsed' && `"${ghost.personality?.catchphrase}"`}
            {isCatchable && '✦ materialised. catchable now.'}
          </div>
        </div>
        {isCatchable ? (
          <button
            onClick={attempt} disabled={busy}
            data-testid="ghost-attempt-btn"
            className="relative z-10 px-4 py-2.5 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 transition disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #ffd700, #ff007f)', color: '#000' }}
          >
            <Lightning size={14} weight="fill" /> {busy ? 'reaching…' : 'Try to catch'}
          </button>
        ) : (
          <div className="relative z-10 text-right text-[10px] uppercase tracking-widest text-white/60">
            <div>not yet</div>
            <div className="text-white/55">wait for materialise</div>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {caughtBubble && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] grid place-items-center bg-black/95 backdrop-blur-xl"
            data-testid="ghost-caught-overlay"
          >
            {Array.from({ length: 60 }).map((_, i) => (
              <motion.span key={i}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{
                  x: Math.cos((i / 60) * Math.PI * 2) * (200 + Math.random() * 250),
                  y: Math.sin((i / 60) * Math.PI * 2) * (200 + Math.random() * 250),
                  opacity: [0, 1, 0], scale: [0, 1, 0],
                }}
                transition={{ duration: 3, delay: i * 0.02 }}
                className="absolute h-2 w-2 rounded-full"
                style={{ background: i % 2 === 0 ? '#ffd700' : '#ff007f' }}
              />
            ))}
            <motion.div
              initial={{ scale: 0.4 }} animate={{ scale: 1 }} exit={{ scale: 1.3, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 180 }}
              className="text-center max-w-md mx-4 relative"
            >
              <div className="text-6xl mb-3">👻</div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-bx-gold flex items-center justify-center gap-1">
                <Crown size={12} weight="fill" /> the network witnessed this
              </div>
              <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-3">
                You caught <span className="shimmer-text">{caughtBubble.ghost_name}</span>
              </h1>
              <p className="text-white/80 mt-3">
                A mythic bubble has joined your collection. +{caughtBubble.xp_gained} XP.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to={`/bubble/${caughtBubble.bubble?.id}`}
                  onClick={() => setCaughtBubble(null)}
                  data-testid="ghost-caught-goto"
                  className="btn-volt px-6 py-3 rounded-full font-bold"
                >See your ghost →</Link>
                <button
                  onClick={() => setCaughtBubble(null)}
                  className="px-6 py-3 rounded-full glass-hi font-bold"
                >Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GhostBanner;
